import {
  Button,
  Card,
  Flexbox,
  Heading,
  Sizes,
  Text,
  TextElementTypes,
} from '@sede-enterprise/shell-ds-react-framework';
import {
  FullScreen,
  FullScreenExit,
} from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import { SiteAvailabilityControl } from '@see-power-pft-common-js/net-availability/enums/site-availability-control';
import { FullScreenModal } from 'components/FullScreenModal/FullScreenModal';
import { FullScreenModalChartWrapper } from 'components/FullScreenModal/FullScreenModal.styled';
import { InfoCircleTooltip } from 'components/Tooltip/InfoCircleTooltip';
import { ReductionsTable } from 'pages/Availability/common/ReductionsTable/ReductionsTable';
import {
  BaseAssetAvailabilityEntry,
  BaseSiteAvailabilityEntry,
} from 'pages/Availability/types';
import { useEffect, useState } from 'react';
import { useStore } from 'store';
import { Timezone } from 'types/timezone';

interface CommonReductionsTableProps {
  previewMode: boolean;
  timezone: Timezone;
}
type ReductionsTableCardProps = CommonReductionsTableProps &
  (
    | {
        availabilityEntries: BaseSiteAvailabilityEntry[];
        availabilityControl: SiteAvailabilityControl.SiteLevel;
      }
    | {
        availabilityEntries: BaseAssetAvailabilityEntry[];
        availabilityControl: SiteAvailabilityControl.AssetLevel;
      }
  );

export const ReductionsTableCard = ({
  availabilityEntries,
  availabilityControl,
  previewMode,
  timezone,
}: ReductionsTableCardProps) => {
  const [appTheme] = useStore((state) => [state.appTheme]);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);

  const toggleFullscreen = () => setIsFullScreenOpen((current) => !current);
  const fullScreenButton = (
    <Button
      variant="outlined"
      data-testid="reductions-table-fullscreen-button"
      icon={isFullScreenOpen ? <FullScreenExit /> : <FullScreen />}
      onClick={toggleFullscreen}
      iconOnly
      style={{ flexShrink: 0 }}
    />
  );

  const headerContent = (
    <Flexbox
      style={{ padding: 24, width: '100%' }}
      justifyContent="space-between"
    >
      <Flexbox
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        style={{ width: '100%' }}
        gap="16px"
      >
        <Flexbox alignItems="center" style={{ flexShrink: 0 }}>
          <Heading
            type={TextElementTypes.Span}
            bold
            level={4}
            prominence={'strong'}
            style={{ paddingRight: '5px' }}
          >
            Reported reductions
          </Heading>
          <InfoCircleTooltip
            tooltip={
              <Text type="span" size={Sizes.Small}>
                Each row represents a separate availability reduction report,
                and actions can be taken on each row by clicking on the menu
                dots to the right of the row.
              </Text>
            }
            infoCircleStyle={{ marginTop: '2px' }}
          />
        </Flexbox>
        <Flexbox alignItems={'center'} gap={'16px'}>
          {fullScreenButton}
        </Flexbox>
      </Flexbox>
    </Flexbox>
  );

  // prevent background scrolling when modal open
  useEffect(() => {
    if (isFullScreenOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isFullScreenOpen]);

  const fullScreenModal = (
    <FullScreenModal
      open={isFullScreenOpen}
      $narrowMobile={false}
      $mobileLandscape={false}
      onClose={toggleFullscreen}
      header={{
        content: (
          <Flexbox
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            style={{
              width: '100%',
            }}
            gap="16px"
          >
            {headerContent}
          </Flexbox>
        ),
      }}
      closeButton={false}
      contentScrollable={false}
      padding={false}
      centered
      size={Sizes.Large}
      $showMask={false}
      $appTheme={appTheme}
      focusTriggerAfterClose
    >
      <FullScreenModalChartWrapper
        $narrowMobile={false}
        style={{
          padding: '16px',
        }}
      >
        <ReductionsTable
          availabilityEntries={availabilityEntries}
          availabilityControl={availabilityControl}
          previewMode={previewMode}
          timezone={timezone}
        />
      </FullScreenModalChartWrapper>
    </FullScreenModal>
  );

  return (
    <Card
      header={{
        content: headerContent,
      }}
    >
      <ReductionsTable
        availabilityEntries={availabilityEntries}
        availabilityControl={availabilityControl}
        previewMode={previewMode}
        timezone={timezone}
      />
      {fullScreenModal}
    </Card>
  );
};
